import type { GoodsReceiptPallet, GoodsReceiptPalletRowLot, UnitOfMeasurement } from "@/graphql/codegen/graphql"

interface ZplParams {
  productName: string
  referenceId: string
  stockNumber: string
  fullPalletUom: string
  singlePackageUom: string
  packageCount: number
  lots: GoodsReceiptPalletRowLot[]
  currentUom: string
}

export const mapZplParams = (
  { goodsReceiptPalletRow: palletRow, referenceId }: GoodsReceiptPallet,
  uoms: UnitOfMeasurement[]
): ZplParams => {
  const lots = palletRow?.lots || []
  const product = palletRow?.product

  return {
    productName: product?.companyProductDescription || "Unknown Product",
    referenceId: String(referenceId),
    stockNumber: product?.publicId || "N/A",
    fullPalletUom: palletRow?.palletUnitQuantity || "N/A",
    singlePackageUom: String(product?.packageUsWeight ?? "N/A"),
    packageCount: lots.reduce((acc, { units }) => acc + (units ?? 0), 0),
    lots,
    currentUom: uoms.find((uom) => uom.id === product?.packageUsWeightUom)?.name || "N/A",
  }
}

export const generatePalletLabelZpl = (pallet: GoodsReceiptPallet, uoms: UnitOfMeasurement[]): string => {
  const { productName, referenceId, stockNumber, fullPalletUom, singlePackageUom, packageCount, lots, currentUom } =
    mapZplParams(pallet, uoms)

  const qrData = JSON.stringify({
    version: 1,
    eventType: "put-away",
    refId: referenceId,
  })

  const lotYOffset = 35
  const lotYStart = 985
  const lotsZpl = lots
    .map(
      (lot, i) => `^FO400,${lotYStart + lotYOffset * i}^A0N,30,30^FD${lot.lotNumber} (${lot.totalUom} ${currentUom})^FS`
    )
    .join("")

  return `^XA
    ^PW800^LL1200^LS0^LT0
    ^CI28~TA000~JSB^MNW^MTD^PMN^JMA^PR4,4~SD20^PA0,0,0,0
    ^FO60,50^A0N,44,50^FB700,2,0,C,0^FD${productName}^FS
    ^FO150,120^BQN,2,14^FDMA,${qrData}^FS
    ^FO150,588^GB463,100,100^FS
    ^FO150,600^A0N,100,130^FB490,2,0,C,0^FR^FD${referenceId}^FS
    ^FO0,725^A0N,46,56^FB800,1,0,C,0^FDStock #: ${stockNumber}^FS
    ^FO20,795^GB760,3,3^FS
    ^FO70,835^A0N,35,35^FDFull Pallet UoM:^FS
    ^FO70,885^A0N,35,35^FDStd Package UoM:^FS
    ^FO70,935^A0N,35,35^FDFull Package Count:^FS
    ^FO70,985^A0N,35,35^FDReceived Lot(s) #:^FS
    ^FO400,835^A0N,35,35^FD${fullPalletUom} ${currentUom}^FS
    ^FO400,885^A0N,35,35^FD${singlePackageUom} ${currentUom}^FS
    ^FO400,935^A0N,35,35^FD${packageCount}^FS
    ${lotsZpl}
    ^XZ`
}
